import { ListChecksIcon, PlusIcon, SearchIcon } from "lucide-react"
import type * as React from "react"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import { ITEM_PAGE_SIZE, useItemFilters, useItemList } from "@/features/items/use-item-list"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useAuth } from "@/features/auth/auth-context"
import { ApiError } from "@/lib/api-error"
import type { Item } from "@/lib/endpoints"
import { formatRupees } from "@/lib/money"
import { basisPointsToInputValue } from "@/lib/units"
import { routes } from "@/routes/paths"

/**
 * My List — the saved jobs (§11.1, §7).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THIS SCREEN IS WHY THE BILL EDITOR IS FAST. Every line on an invoice must
 * carry an HSN/SAC code and a GST rate (§4.1), and nobody remembers either.
 * Saving a job once turns the next bill into two taps instead of typing a code
 * the owner would otherwise guess — and a guessed code on a tax invoice is the
 * kind of mistake a CA finds a year later.
 *
 * ORDERED BY HOW OFTEN IT IS BILLED, by the server (`ITEM_PICKER_ORDER_BY`). A
 * fabricator bills the same dozen jobs weekly; alphabetical order would bury
 * them under everything he has ever done once.
 *
 * RETIRED JOBS ARE HIDDEN BY DEFAULT AND NEVER DELETED. Past invoice lines still
 * reference the row, so removing one would orphan documents that have already
 * been issued. The "Show" filter brings them back.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function ItemListScreen(): React.JSX.Element {
  const { t } = useTranslation()
  const { can } = useAuth()

  const { filters, setFilters, clearFilters, activeCount } = useItemFilters()
  const query = useItemList(filters)

  const rows = query.data?.items ?? []
  const total = query.data?.total ?? 0
  const first = total === 0 ? 0 : filters.offset + 1
  const last = Math.min(filters.offset + rows.length, total)

  return (
    <div className="flex flex-col gap-4 p-4 md:gap-6 md:p-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t("nav.items")}</h1>
          <p className="text-muted-foreground text-sm">{t("item.listHelp")}</p>
        </div>
        {can("item.create") ? (
          <Button nativeButton={false} render={<Link to={routes.itemNew} />}>
            <PlusIcon />
            {t("btn.addNewItem")}
          </Button>
        ) : null}
      </header>

      <ItemFilterBar filters={filters} setFilters={setFilters} />

      {query.isError ? (
        <Alert variant="destructive">
          <AlertTitle>{t("err.couldNotLoad")}</AlertTitle>
          <AlertDescription className="flex flex-col items-start gap-3">
            <span>
              {query.error instanceof ApiError ? query.error.message : t("err.somethingWentWrong")}
            </span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                void query.refetch()
              }}
            >
              {t("btn.retry")}
            </Button>
          </AlertDescription>
        </Alert>
      ) : query.isPending ? (
        <ListSkeleton />
      ) : rows.length === 0 ? (
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <ListChecksIcon />
            </EmptyMedia>
            <EmptyTitle>{activeCount > 0 ? t("item.noneFound") : t("item.emptyTitle")}</EmptyTitle>
            <EmptyDescription>
              {activeCount > 0 ? t("item.noneFoundHelp") : t("item.emptyHelp")}
            </EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            {activeCount > 0 ? (
              <Button variant="outline" onClick={clearFilters}>
                {t("list.clearFilters")}
              </Button>
            ) : can("item.create") ? (
              <Button nativeButton={false} render={<Link to={routes.itemNew} />}>
                <PlusIcon />
                {t("btn.addNewItem")}
              </Button>
            ) : null}
          </EmptyContent>
        </Empty>
      ) : (
        <>
          <Card className="overflow-hidden py-0 md:hidden">
            <ul className="flex flex-col">
              {rows.map((item) => (
                <li key={item.id} className="border-b last:border-b-0">
                  <Link
                    to={routes.item(item.id)}
                    className="hover:bg-accent/50 flex min-h-14 items-center gap-3 p-4 transition-colors"
                  >
                    <span className="flex min-w-0 flex-1 flex-col gap-1">
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="font-medium">{item.name}</span>
                        <RetiredMark item={item} />
                      </span>
                      <span className="text-muted-foreground text-xs tabular-nums">
                        {t("item.codeChip", {
                          code: item.hsn,
                          rate: basisPointsToInputValue(item.gstRateBp),
                        })}
                      </span>
                    </span>
                    <span className="shrink-0 text-right">
                      <span className="block font-medium tabular-nums">
                        {formatRupees(item.ratePaise)}
                      </span>
                      <span className="text-muted-foreground block text-xs">
                        {t("item.perUnit", { unit: item.unitLabel })}
                      </span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </Card>

          <Card className="hidden overflow-hidden py-0 md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("lbl.jobName")}</TableHead>
                  <TableHead>HSN/SAC</TableHead>
                  <TableHead className="text-right">{t("line.gstRate")}</TableHead>
                  <TableHead>{t("lbl.unit")}</TableHead>
                  <TableHead className="text-right">{t("lbl.rate")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((item) => (
                  // `relative` — the stretched link resolves against the ROW.
                  <TableRow key={item.id} className="hover:bg-accent/50 relative cursor-pointer">
                    <TableCell className="font-medium">
                      <Link
                        to={routes.item(item.id)}
                        className="after:absolute after:inset-0 hover:underline"
                      >
                        {item.name}
                      </Link>
                      <RetiredMark item={item} className="ml-2" />
                    </TableCell>
                    <TableCell className="tabular-nums">{item.hsn}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      {t("item.percent", { rate: basisPointsToInputValue(item.gstRateBp) })}
                    </TableCell>
                    <TableCell>{item.unitLabel}</TableCell>
                    <TableCell className="text-right font-medium tabular-nums whitespace-nowrap">
                      {formatRupees(item.ratePaise)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>

          <Pager
            first={first}
            last={last}
            total={total}
            offset={filters.offset}
            onOffset={(offset) => {
              setFilters({ offset })
              window.scrollTo({ top: 0, behavior: "smooth" })
            }}
          />
        </>
      )}
    </div>
  )
}

/** Taken off the list. Never deleted — issued bills still reference it. */
function RetiredMark({
  item,
  className,
}: {
  item: Item
  className?: string
}): React.JSX.Element | null {
  const { t } = useTranslation()
  if (item.isActive) return null

  return (
    <Badge variant="secondary" className={`text-xs ${className ?? ""}`}>
      {t("item.retired")}
    </Badge>
  )
}

/**
 * THE "SHOW" FILTER IS NO LONGER A NATIVE `<select>` (2026-08-16, at the
 * owner's request, for visual consistency with the rest of the UI). The reason
 * the native one was chosen is worth keeping: on a phone it opened the
 * operating system's own wheel, which is reliably hittable without the page
 * having to draw a 48px target (§11.1). That target is now drawn by hand, as
 * `` on the item and on the trigger — the rem class rather than a pixel
 * literal so it grows with the large-text preference. DO NOT TIDY IT AWAY. The
 * full version of this note is above `InvoiceFilterBar` in the bill register.
 */
function ItemFilterBar({
  filters,
  setFilters,
}: {
  filters: ReturnType<typeof useItemFilters>["filters"]
  setFilters: ReturnType<typeof useItemFilters>["setFilters"]
}): React.JSX.Element {
  const { t } = useTranslation()
  const [text, setText] = useState(filters.q ?? "")

  /*
   * TWO OPTIONS, AND DELIBERATELY NO "true". Empty sends NO `active` parameter,
   * which the server defaults to `true` — "a deactivated job should never
   * surface in the picker". The default is not duplicated here, so it cannot
   * drift: `active=true` would be a second copy of a server rule and `active=`
   * would be a 400.
   *
   * The array is also handed to `<Select items={…}>`, which is how Base UI
   * resolves the trigger's text; without it the trigger prints the raw value and
   * this control would read "false".
   */
  const showOptions = [
    { value: "", label: t("item.showActive") },
    { value: "false", label: t("item.showRetired") },
  ]

  useEffect(() => {
    setText(filters.q ?? "")
  }, [filters.q])

  // Debounced: the server matches name, description AND the HSN code, so this
  // is a real search over the whole list rather than a filter over one page.
  useEffect(() => {
    const current = filters.q ?? ""
    if (text === current) return
    const timer = window.setTimeout(() => {
      setFilters({ q: text.trim() === "" ? undefined : text.trim() })
    }, 300)
    return () => {
      window.clearTimeout(timer)
    }
  }, [text, filters.q, setFilters])

  return (
    <div className="flex flex-wrap items-end gap-3">
      <div className="flex min-w-56 flex-1 flex-col gap-1.5 sm:max-w-sm">
        <Label htmlFor="item-search">{t("item.searchLabel")}</Label>
        <div className="relative">
          <SearchIcon
            aria-hidden="true"
            className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2"
          />
          <Input
            id="item-search"
            type="search"
            className="pl-9"
            placeholder={t("item.searchPlaceholder")}
            value={text}
            onChange={(event) => {
              setText(event.target.value)
            }}
          />
        </div>
      </div>

      <div className="flex min-w-44 flex-col gap-1.5">
        <Label htmlFor="item-active">{t("item.showLabel")}</Label>
        <Select
          items={showOptions}
          value={filters.active === undefined ? "" : String(filters.active)}
          onValueChange={(value) => {
            /* The value is a STRINGIFIED BOOLEAN in both directions — the filter
             * itself is `boolean | undefined`. Base UI hands the string over
             * directly (there is no `event.target`) and types it nullable, so
             * null folds into the same "no filter" branch as "". */
            setFilters({ active: value === "" || value == null ? undefined : value === "true" })
          }}
        >
          {/* The id goes on the TRIGGER, which is the button `<Label htmlFor>`
            * above labels — a <button> is a labelable element, so
            * `getByLabelText` still resolves this control. */}
          <SelectTrigger id="item-active" className="w-full">
            {/* The same string as the "" item: Base UI reads an empty value as
              * NO value, so without this the trigger would show a greyed-out
              * placeholder where the native select showed the default. */}
            <SelectValue placeholder={t("item.showActive")} />
          </SelectTrigger>
          {/* `w-auto` because the popup otherwise matches the trigger exactly and
            * clips "Jobs I have taken off"; the OS wheel sized itself. */}
          <SelectContent className="w-auto min-w-(--anchor-width) max-w-[min(90vw,28rem)]">
            {showOptions.map((option) => (
              <SelectItem key={option.value} value={option.value} className="pl-3">
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  )
}

function Pager({
  first,
  last,
  total,
  offset,
  onOffset,
}: {
  first: number
  last: number
  total: number
  offset: number
  onOffset: (offset: number) => void
}): React.JSX.Element | null {
  const { t } = useTranslation()
  if (total <= ITEM_PAGE_SIZE) return null

  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <p className="text-muted-foreground text-sm tabular-nums">
        {t("list.showingRange", { first, last, total })}
      </p>
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={offset === 0}
          onClick={() => {
            onOffset(Math.max(0, offset - ITEM_PAGE_SIZE))
          }}
        >
          {t("btn.previous")}
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={last >= total}
          onClick={() => {
            onOffset(offset + ITEM_PAGE_SIZE)
          }}
        >
          {t("btn.next")}
        </Button>
      </div>
    </div>
  )
}

function ListSkeleton(): React.JSX.Element {
  return (
    <Card className="overflow-hidden py-0">
      <ul className="flex flex-col">
        {[0, 1, 2, 3, 4].map((index) => (
          <li key={index} className="flex items-center gap-3 border-b p-4 last:border-b-0">
            <div className="flex flex-1 flex-col gap-2">
              <Skeleton className="h-4 w-44" />
              <Skeleton className="h-3 w-28" />
            </div>
            <Skeleton className="h-4 w-20" />
          </li>
        ))}
      </ul>
    </Card>
  )
}
